import React, { FunctionComponent, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { getMonthValue, getPeriodDate, getPreviousMonth, IMonthValue } from '../util/DateUtils';
import { CalendarInput } from './CalendarInput';
import '../assets/styles/datepicker.css';
import { injectIntl } from 'react-intl';
import fi from 'date-fns/locale/fi';

interface IDateSelectorProps {
  startPeriod: IMonthValue;
  endPeriod?: IMonthValue;
  isStartAndEnd?: boolean;
  onChange: (start: IMonthValue, end: IMonthValue) => void;
  dateFormat: string; // 'yyyy', 'yyyy, QQQ', 'yyyy-MM' or 'y-MM-dd'
  disabled?: boolean;
  intl: any;
}

const popperModifiers = [
  {
    name: "offset",
    options: {
      offset: [0, -30],
    },
  }
];

export const DateSelector: FunctionComponent<IDateSelectorProps> = (props) => {
    const [inputStartValue, setInputStartValue] = useState<IMonthValue>(props.startPeriod);
    const [selectedStartDay, setSelectedStartDay] = useState(getPeriodDate(props.startPeriod));
    const [inputEndValue, setInputEndValue] = useState<IMonthValue>(props.endPeriod || getPreviousMonth());
    const [selectedEndDay, setSelectedEndDay] = useState(props.endPeriod ? getPeriodDate(props.endPeriod) : undefined);
    const [startMinDate, ] = useState(new Date('01-01-2019'));
    const [startMaxDate, setStartMaxDate] = useState(selectedEndDay ? selectedEndDay : new Date());
    const [endMinDate, setEndMinDate] = useState(selectedStartDay ? selectedStartDay : new Date('01-01-2019'));
    const [endMaxDate, ] = useState(new Date());

    const startTextId = props.isStartAndEnd ? 'calendar.select.start' : 'calendar.select.month';

    registerLocale(props.intl.locale, fi);

    const header = (date: Date, increaseYear: any, decreaseYear: any, min: Date, max: Date) => {
      return (
        <div>
          <button className={"react-datepicker__navigation react-datepicker__navigation--previous"}
                  style={min.getFullYear() === date.getFullYear() || props.dateFormat === 'yyyy' ? 
                         { visibility: "hidden" } : undefined}
                  onClick={decreaseYear}>
            <span className={"react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"}>
              {"<"}
            </span>
          </button>
          <span className="react-datepicker__current-month">{date.getFullYear()}</span>
          <button className={"react-datepicker__navigation react-datepicker__navigation--next"}
                  style={max.getFullYear() === date.getFullYear() || props.dateFormat === 'yyyy'  ? 
                         { visibility: "hidden" } : undefined} 
                  onClick={increaseYear}>
            <span className={"react-datepicker__navigation-icon react-datepicker__navigation-icon--next"}>
              {">"}
            </span>
          </button>
        </div>
      )
    }

    return (
      <>
        <div className="form-group" style={ props.disabled ? { opacity: '50%' } : undefined}>
          <div className="d-flex flex-md-row align-items-md-center flex-column justify-content-md-start">
            <DatePicker
              renderCustomHeader={({ date, decreaseYear, increaseYear }) => header(date, increaseYear, decreaseYear, startMinDate, startMaxDate) }
              showPopperArrow={false}
              selected={selectedStartDay}
              onChange={(date: any) => {
                setSelectedStartDay(date ? date : undefined);
                setEndMinDate(date ? date : new Date('01-01-2019'))
                setInputStartValue(getMonthValue(new Date(date)));
                props.onChange(getMonthValue(new Date(date)), inputEndValue);
              }}
              customInput={<CalendarInput nameId='startdate' disabled={props.disabled} hideLabel={true} label={startTextId} />}
              dateFormat={props.dateFormat} // "yyyy-MM-dd"
              showMonthYearPicker={props.dateFormat==='yyyy-MM'}
              showYearPicker={props.dateFormat==='yyyy'}
              showQuarterYearPicker={props.dateFormat==="yyyy, QQQ"}
              popperModifiers={popperModifiers}
              locale={props.intl.locale}
              disabled={props.disabled}
              minDate={startMinDate}
              maxDate={startMaxDate}
            />
            {props.isStartAndEnd &&
              <div className="rdp-custom-padding">
                <DatePicker
                  renderCustomHeader={({ date, decreaseYear, increaseYear }) => header(date, increaseYear, decreaseYear, endMinDate, endMaxDate) }
                  showPopperArrow={false}
                  selected={selectedEndDay}
                  onChange={(date: any) => {
                    setSelectedEndDay(date ? date : undefined);
                    setStartMaxDate(date ? date : new Date());
                    setInputEndValue(getMonthValue(new Date(date)));
                    props.onChange(inputStartValue, getMonthValue(new Date(date)));
                  }}
                  customInput={<CalendarInput nameId='enddate' disabled={props.disabled} hideLabel={true} label="calendar.select.end" />}
                  dateFormat={props.dateFormat}
                  showMonthYearPicker={props.dateFormat==='yyyy-MM'}
                  showYearPicker={props.dateFormat==='yyyy'}
                  showQuarterYearPicker={props.dateFormat==="yyyy, QQQ"}
                  popperModifiers={popperModifiers}
                  locale={props.intl.locale}
                  disabled={props.disabled}
                  minDate={endMinDate}
                  maxDate={endMaxDate}
                />
              </div>
            }
          </div>
        </div>
      </>
    );
};

export default injectIntl(DateSelector);